import { createFileRoute, Outlet, Link, useNavigate } from '@tanstack/react-router';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FiLogOut } from 'react-icons/fi';
import { identifyUser, track } from '@/lib/analytics';
import { apiJson } from '@/lib/api';
import type { UserResponse } from '@/lib/types';

function ProLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const clerk = useClerk();
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    track('Pro Dashboard');
  }, []);

  const professionalUserQuery = useQuery({
    queryKey: ['professional-user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/professional/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!professionalUserQuery.data) return;
    identifyUser({
      id: professionalUserQuery.data.id,
      created_at: professionalUserQuery.data.created_at,
      first_name: professionalUserQuery.data.first_name,
      last_name: professionalUserQuery.data.last_name,
      email: professionalUserQuery.data.email,
      phone_number: professionalUserQuery.data.phone_number,
      subscribed: professionalUserQuery.data.subscribed,
    });
  }, [professionalUserQuery.data]);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    navigate({ to: '/auth/pro/sign-in' });
  }, [isLoaded, isSignedIn, navigate]);

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await clerk.signOut();
      navigate({ to: '/auth/pro/sign-in' });
    }
  };

  if (!isLoaded) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  const primaryEmail = user?.primaryEmailAddress?.emailAddress;

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <header className='border-b border-gray-200 bg-white'>
        <div className='max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4'>
          <Link
            to='/pro'
            className='flex items-center gap-2 text-primary text-xl font-light'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            <img src='/ship.svg' alt='' className='w-5 h-5 mb-2' />
            Harbor
            <span
              className='text-sm font-semibold text-gray-500 ml-1'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Professional
            </span>
          </Link>
          <div className='flex items-center gap-3 min-w-0'>
            {primaryEmail && (
              <span
                className='text-sm text-gray-600 truncate max-w-[200px] hidden sm:inline'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {primaryEmail}
              </span>
            )}
            <button
              type='button'
              onClick={handleSignOut}
              className='flex items-center gap-2 px-3 py-2 rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-semibold text-sm shrink-0'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              <FiLogOut className='w-5 h-5' />
              <span className='hidden sm:inline'>Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <main className='flex-1'>
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/pro')({
  component: ProLayout,
});
